import { useRef, useState } from "react";
import type { ScoringMode } from "../../shared/types";
import { api, ApiError, generateQuizStream, type QuizSaveBody } from "../api";
import { navigate } from "../router";
import { Ico } from "./shell";
import { AtlModalOverlay, AtlModalShell, SparkleIcon } from "./atelier";

const TYPE_LABEL: Record<string, string> = { mc: "Multiple choice", truefalse: "True / false", multi: "Multi-select" };
/** Draft this many MORE than the requested count, so there's a pool to pick from. */
const SUGGEST_EXTRA = 5;

const PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation";

function inferType(file: File): string {
  if (file.type) return file.type;
  const n = file.name.toLowerCase();
  if (n.endsWith(".pptx")) return PPTX;
  if (n.endsWith(".pdf")) return "application/pdf";
  if (n.endsWith(".png")) return "image/png";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "image/jpeg";
  return "application/octet-stream";
}

const STAGES = ["Reading your document…", "Drafting questions…", "Plating the answers…", "Almost out of the oven…"];

export default function AiModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(10);
  const [scoring, setScoring] = useState<ScoringMode>("speed");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState<QuizSaveBody | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  if (!open) return null;

  async function generate() {
    if (busy || (!topic.trim() && !file)) return;
    if (file && file.size > 75 * 1024 * 1024) {
      setErr("That file's quite large (over 75 MB) — try a smaller one or split it.");
      return;
    }
    setBusy(true);
    setErr("");
    setElapsed(0);
    setProgress({ done: 0, total: 0 });
    const controller = new AbortController();
    abortRef.current = controller;
    const startedAt = Date.now();
    const ticker = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 500);
    try {
      let sourceKey: string | undefined;
      if (file) {
        const ct = inferType(file);
        const up = await api.upload(file, ct, controller.signal);
        sourceKey = up.key;
      }
      // Ask for a few extra so there's a pool to choose from; stream progress.
      const draftQuiz = await generateQuizStream(
        { topic: topic.trim() || undefined, count: count + SUGGEST_EXTRA, scoring, sourceKey },
        (done, total) => setProgress({ done, total }),
        controller.signal,
      );
      clearInterval(ticker);
      // Show the generated questions for review; pre-tick the requested number,
      // leaving the extras as alternatives the host can swap in.
      setDraft(draftQuiz);
      setSelected(new Set(draftQuiz.questions.slice(0, count).map((_, i) => i)));
      setBusy(false);
    } catch (e) {
      clearInterval(ticker);
      // Cancelled by the host — just return to the form, no error.
      if (controller.signal.aborted) {
        setBusy(false);
        return;
      }
      setErr(e instanceof ApiError ? e.message : "Generation failed — give it another go.");
      setBusy(false);
    }
  }

  // Create the quiz from only the ticked questions, then open it in the builder.
  async function addSelected() {
    if (!draft || saving) return;
    const questions = draft.questions.filter((_, i) => selected.has(i));
    if (questions.length === 0) return;
    setSaving(true);
    setErr("");
    try {
      const { quiz } = await api.createQuiz(draft.title || "AI-generated quiz", draft.scoring, "ai");
      await api.saveQuiz(quiz.id, {
        title: draft.title,
        description: draft.description,
        scoring: draft.scoring,
        status: "draft",
        questions,
      });
      navigate(`/builder/${quiz.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't save those questions — try again.");
      setSaving(false);
    }
  }

  if (busy) {
    // Real progress once questions stream in; a gentle elapsed-based creep
    // beforehand (while the model reads the document).
    const { done, total } = progress;
    const pct = total > 0 && done > 0 ? Math.min(99, Math.round((done / total) * 100)) : Math.min(12, elapsed * 2);
    const stage = done === 0 ? 0 : done < total ? 1 : 2;
    const elapsedLabel = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${String(elapsed % 60).padStart(2, "0")}s`;
    return (
      <AtlModalOverlay>
        <div
          className="atl-card"
          style={{ maxWidth: 520, width: "100%", margin: "0 auto", padding: 26, boxShadow: "0 30px 80px rgba(40,25,15,.3)" }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "30px 10px", gap: 16 }}>
            <span
              style={{
                width: 86,
                height: 86,
                borderRadius: 26,
                background: "var(--atl-gold-soft)",
                color: "var(--atl-gold)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <SparkleIcon size={44} />
            </span>
            <h3 className="atl-serif" style={{ fontSize: 23 }}>
              Baking your quiz…
            </h3>
            <p style={{ color: "var(--atl-soft)", fontWeight: 500 }}>{STAGES[stage]}</p>
            <div style={{ width: 240, height: 8, borderRadius: 8, background: "var(--atl-surface-2)", border: "1px solid var(--atl-line)", overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  borderRadius: 8,
                  background: "linear-gradient(90deg, var(--atl-gold), var(--atl-accent))",
                  transition: "width .5s ease",
                }}
              />
            </div>
            <div className="atl-mono" style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 700 }}>
              {done > 0 ? `Drafted ${done} of ${total} · ${elapsedLabel}` : `${elapsedLabel} elapsed`}
            </div>
            {done === 0 && elapsed >= 12 && (
              <p style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500, maxWidth: 330 }}>
                Reading your {file ? "document" : "topic"} — bigger files take a moment before questions start appearing.
              </p>
            )}
            <button
              className="atl-btn atl-btn--ghost"
              style={{ marginTop: 8, padding: "8px 18px", fontSize: 13 }}
              onClick={() => abortRef.current?.abort()}
            >
              Cancel
            </button>
          </div>
        </div>
      </AtlModalOverlay>
    );
  }

  if (draft) {
    const allOn = selected.size === draft.questions.length;
    const toggle = (i: number) =>
      setSelected((s) => {
        const n = new Set(s);
        n.has(i) ? n.delete(i) : n.add(i);
        return n;
      });
    return (
      <AtlModalOverlay onClose={saving ? undefined : onClose}>
        <div
          className="atl-card"
          style={{ maxWidth: 560, width: "100%", margin: "0 auto", padding: 26, boxShadow: "0 30px 80px rgba(40,25,15,.3)", display: "flex", flexDirection: "column", maxHeight: "86vh" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <span style={{ width: 44, height: 44, borderRadius: 13, background: "var(--atl-gold-soft)", color: "var(--atl-gold)", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
              <SparkleIcon size={22} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 className="atl-serif" style={{ fontSize: 21 }}>
                Pick your questions
              </h3>
              <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, marginTop: 2 }}>
                We drafted {draft.questions.length} from your {file ? "document" : "topic"} — pick the {count} you want, or swap in any others.
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0 10px" }}>
            <span className="atl-chip atl-chip--accent" style={{ fontSize: 12 }}>
              <Ico name="check" size={13} /> {selected.size} selected
            </span>
            <div style={{ flex: 1 }} />
            <button
              className="atl-btn atl-btn--link"
              style={{ fontSize: 12.5 }}
              onClick={() => setSelected(allOn ? new Set() : new Set(draft.questions.map((_, i) => i)))}
            >
              {allOn ? "Clear all" : "Select all"}
            </button>
          </div>

          <div className="bk-scroll" style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
            {draft.questions.map((q, i) => {
              const on = selected.has(i);
              const correct = q.options.filter((o) => o.correct).map((o) => o.text.trim()).filter(Boolean);
              return (
                <div
                  key={i}
                  onClick={() => toggle(i)}
                  className="atl-row"
                  style={{ alignItems: "flex-start", gap: 12, cursor: "pointer", borderColor: on ? "var(--atl-c)" : "var(--atl-line)", background: on ? "#f3f7ef" : "var(--atl-surface)" }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 7,
                      flex: "0 0 auto",
                      marginTop: 1,
                      display: "grid",
                      placeItems: "center",
                      background: on ? "var(--atl-c)" : "transparent",
                      color: "#fff",
                      border: on ? "none" : "1.5px solid var(--atl-line)",
                    }}
                  >
                    {on && <Ico name="check" size={14} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14.5, lineHeight: 1.35 }}>{q.prompt}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6, alignItems: "center" }}>
                      <span className="atl-mono" style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 99, border: "1px solid var(--atl-line)", color: "var(--atl-soft)" }}>
                        {TYPE_LABEL[q.type] ?? q.type}
                      </span>
                      {q.category && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--atl-faint)" }}>● {q.category}</span>}
                    </div>
                    {correct.length > 0 && (
                      <div style={{ fontSize: 12.5, color: "var(--atl-soft)", fontWeight: 500, marginTop: 6 }}>
                        <b style={{ color: "var(--atl-c)", fontWeight: 700 }}>{correct.length > 1 ? "Answers" : "Answer"}:</b> {correct.join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {err && (
            <div className="atl-chip atl-chip--accent" style={{ justifyContent: "center", padding: "10px 12px", fontWeight: 600, marginTop: 12 }}>
              {err}
            </div>
          )}

          <button
            className="atl-btn atl-btn--primary atl-btn--lg atl-btn--block"
            style={{ marginTop: 16 }}
            disabled={selected.size === 0 || saving}
            onClick={addSelected}
          >
            {saving ? "Adding…" : `Add ${selected.size} question${selected.size === 1 ? "" : "s"} →`}
          </button>
          <button
            className="atl-btn atl-btn--block"
            style={{ background: "transparent", boxShadow: "none", color: "var(--atl-soft)", marginTop: 6 }}
            disabled={saving}
            onClick={() => setDraft(null)}
          >
            ↻ Start over
          </button>
        </div>
      </AtlModalOverlay>
    );
  }

  return (
    <AtlModalOverlay onClose={onClose}>
      <AtlModalShell
        icon={<SparkleIcon size={22} />}
        iconBg="var(--atl-gold-soft)"
        iconFg="var(--atl-gold)"
        title="Generate a quiz with AI"
        sub="Describe a topic, or upload a PDF / PowerPoint to pull questions from."
        onClose={onClose}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 18 }}>
          <div>
            <div className="atl-eyebrow" style={{ marginBottom: 8 }}>
              TOPIC OR INSTRUCTIONS
            </div>
            <textarea
              className="atl-textarea"
              rows={2}
              style={{ resize: "none" }}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Food safety basics for new starters — fridge temps, allergens, hand-washing"
              autoFocus
            />
          </div>

          <div>
            <div className="atl-eyebrow" style={{ marginBottom: 8 }}>
              SOURCE DOCUMENT · OPTIONAL · PDF OR POWERPOINT
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/*"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <>
                <div className="atl-row" style={{ padding: "11px 15px", borderStyle: "dashed" }}>
                  <Ico name="bank" size={16} />
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {file.name}
                  </span>
                  <span className="atl-link2" style={{ fontSize: 13 }} onClick={() => setFile(null)}>
                    Remove
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500, marginTop: 8 }}>
                  Questions and correct answers will be drawn from this document.
                </div>
              </>
            ) : (
              <button className="atl-btn atl-btn--ghost" style={{ alignSelf: "flex-start" }} onClick={() => inputRef.current?.click()}>
                <Ico name="plus" size={15} /> Upload PDF / PowerPoint
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div className="atl-eyebrow" style={{ marginBottom: 8 }}>
                QUESTIONS TO KEEP
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[5, 10, 15].map((n) => {
                  const on = count === n;
                  return (
                    <div
                      key={n}
                      className="atl-mono"
                      onClick={() => setCount(n)}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        padding: "10px 0",
                        fontSize: 13.5,
                        fontWeight: 700,
                        borderRadius: 9,
                        cursor: "pointer",
                        background: on ? "#fbf2f3" : "var(--atl-surface)",
                        color: on ? "var(--atl-accent)" : "var(--atl-soft)",
                        border: `1px solid ${on ? "#e2c5ca" : "var(--atl-line)"}`,
                      }}
                    >
                      {n}
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div className="atl-eyebrow" style={{ marginBottom: 8 }}>
                SCORING
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {(["speed", "flat"] as ScoringMode[]).map((m) => {
                  const on = scoring === m;
                  return (
                    <div
                      key={m}
                      onClick={() => setScoring(m)}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        padding: "10px 0",
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 9,
                        cursor: "pointer",
                        textTransform: "capitalize",
                        background: on ? "#fbf5e6" : "var(--atl-surface)",
                        color: on ? "#8a6a23" : "var(--atl-soft)",
                        border: `1px solid ${on ? "#e7d6ac" : "var(--atl-line)"}`,
                      }}
                    >
                      {m}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {err && (
            <div
              className="atl-chip atl-chip--accent"
              style={{ justifyContent: "center", padding: "10px 12px", fontWeight: 600 }}
            >
              {err}
            </div>
          )}

          <button className="atl-btn atl-btn--primary atl-btn--lg atl-btn--block" disabled={!topic.trim() && !file} onClick={generate}>
            <SparkleIcon size={16} /> Generate quiz
          </button>
          <div style={{ textAlign: "center", fontSize: 12, color: "var(--atl-faint)", fontWeight: 600 }}>
            We'll draft a few extra so you can pick your favourites — nothing's added until you choose.
          </div>
        </div>
      </AtlModalShell>
    </AtlModalOverlay>
  );
}
